/**
 * THE COMPANY'S CLOCK, ON THE SCREEN. The web twin of apps/api/src/lib/tenantDate.ts.
 *
 * Shane, 2026-10-01: *"we need one timezone clock that feeds all"*.
 *
 * WHY THIS EXISTS. Admin > Time zone tells a director: "Every time the platform
 * shows — clock-ins, timesheets, shifts, documents — is read in this zone."
 * The API keeps that promise through tenantDate.ts. The screens did not: every
 * `toLocaleDateString` / `toLocaleTimeString` formats in the VIEWER's browser
 * zone, so a clock-in at 00:37 London shows 00:37 to a UK browser while a New
 * York company's own clock says 19:37 the evening before. Only the zone the
 * company chose is right for every viewer; the browser's is right only by
 * coincidence.
 *
 * WHAT IT GIVES A SCREEN. `useCompanyClock()` returns formatters that always
 * format in the company's zone. The zone is not something a caller can pass:
 * a `timeZone` in the options is overwritten, because the zone IS the point.
 * The defaults are the formats screens already use most (`02 Oct 2026`,
 * `00:37`), so moving a screen onto the clock changes the zone and nothing
 * else.
 *
 * AN INSTANT IS NOT A DAY. `formatDate` / `formatTime` / `formatDateTime` are
 * for instants: a clock-in, an issue time, a `createdAt`. A calendar date — a
 * `@db.Date` such as a shift's date or an effective date — has no time and no
 * zone, and arrives as `2026-10-01` or `2026-10-01T00:00:00.000Z`. Formatted
 * as an instant in a zone west of UTC, that is 30 September. Calendar dates go
 * through `formatCalendarDate`, which never moves the day. Choosing between
 * the two is the one decision a caller has to make.
 *
 * WHERE THE ZONE COMES FROM. `GET /api/tenant/time-zone`, under the same React
 * Query key Admin > Time zone uses — so that page's half-minute re-read
 * refreshes this too. The company does not set its zone (TODO 257): JeanieIQ
 * does, in Platform Admin. The server is on the new zone at the tenant's next
 * request; a screen already open re-reads it the next time the tab is looked
 * at or a screen is opened, once what it holds is five minutes old (the
 * staleTime below), and within half a minute while Admin > Time zone is open.
 * Until it has loaded, the clock reads the platform default, the GB country
 * pack's zone: the same default tenantDate.ts falls back to on the API.
 *
 * The locale is `DEFAULT_LOCALE` from @jeanieiq/i18n, which every screen
 * hard-codes as 'en-GB' today. This file moves the zone, not the format.
 */
import { useQuery } from '@tanstack/react-query';
import { gb } from '@jeanieiq/country-packs';
import { DEFAULT_LOCALE } from '@jeanieiq/i18n';
import { apiFetch } from './api';
import { queryClient } from './queryClient';

/** The zone the clock reads before the company's own has loaded. */
export const DEFAULT_COMPANY_TIME_ZONE = gb.timezone;

/**
 * Shared with TimeZoneAdminPage, which re-reads it every half minute. The two
 * must stay the same key: companyClock.test.tsx renders the real page beside
 * the clock to prove a changed zone reaches both from one read.
 */
export const COMPANY_TIME_ZONE_KEY = ['tenant', 'time-zone'] as const;

/** An instant as the API sends it, or as a screen holds it. */
export type When = Date | string | number;

/** The formats screens use most, so a screen that moves keeps its look. */
const DATE: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
const TIME: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' };
const DATE_TIME: Intl.DateTimeFormatOptions = { ...DATE, ...TIME };

const toDate = (at: When): Date => (at instanceof Date ? at : new Date(at));

/**
 * `at`, formatted in `timeZone`. Any `timeZone` in `options` is overwritten.
 *
 * An unreadable value gives exactly what `toLocaleDateString` gives for it
 * today rather than throwing - `Intl.DateTimeFormat.format` throws on an
 * invalid date, and a screen moved onto the clock must not start crashing on
 * data it used to survive.
 */
export function formatInZone(
  timeZone: string,
  at: When,
  options: Intl.DateTimeFormatOptions,
  locale: string = DEFAULT_LOCALE,
): string {
  const d = toDate(at);
  if (Number.isNaN(d.getTime())) return d.toLocaleDateString(locale);
  return new Intl.DateTimeFormat(locale, { ...options, timeZone }).format(d);
}

/**
 * The company's calendar date at `at`, as `yyyy-mm-dd`. The twin of the API's
 * `tenantLocalDateIso` - compare these as strings, never build an instant to
 * stand for a day. `en-CA` only because it formats as yyyy-mm-dd.
 */
export function companyDateIso(timeZone: string, at: When = new Date()): string {
  return formatInZone(timeZone, at, { year: 'numeric', month: '2-digit', day: '2-digit' }, 'en-CA');
}

/** How far `timeZone`'s wall clock is ahead of UTC at `at`. The twin of the API's `offsetMsAt`. */
function offsetMsAt(timeZone: string, at: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const get = (t: string): number => Number(parts.find((p) => p.type === t)?.value);
  const asIfUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour') % 24, get('minute'), get('second'));
  return asIfUtc - at.getTime();
}

/**
 * The INSTANT the company's wall clock reads `hhmm` on day `ymd`. The twin of
 * the API's `tenantLocalInstant`, with its two-pass offset, so a time typed on
 * the day the clocks change still lands on that time on the clock. For a time a
 * person types as the company's time: never `new Date(\`${ymd}T${hhmm}\`)`, which
 * is the viewer's computer's.
 */
export function companyLocalInstant(timeZone: string, ymd: string, hhmm: string): Date {
  const asIfUtc = Date.parse(`${ymd}T${hhmm.slice(0, 5)}:00Z`);
  if (Number.isNaN(asIfUtc)) return new Date(NaN);
  const first = asIfUtc - offsetMsAt(timeZone, new Date(asIfUtc));
  return new Date(asIfUtc - offsetMsAt(timeZone, new Date(first)));
}

/** The company's wall-clock time at `at`, as `hh:mm` on the 24-hour clock - what a time input holds. */
export function companyTimeOfDay(timeZone: string, at: When): string {
  return formatInZone(timeZone, at, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }, 'en-GB');
}

/**
 * The company's zone, for code outside a component - a module-level helper, an
 * HTML document builder, a print stamp - where `useCompanyClock()` cannot run.
 *
 * It reads the zone from the app's one React Query cache - the same entry
 * `useCompanyClock()` and Admin > Time zone share, which AppShell loads on
 * every signed-in page. Until that has loaded it reads the platform default,
 * exactly as the hook does. A component should use the hook instead: the hook
 * re-renders when the zone arrives, a read of the cache does not (TODO 135).
 */
export function companyTimeZone(): string {
  return queryClient.getQueryData<{ timezone: string }>(COMPANY_TIME_ZONE_KEY)?.timezone
    ?? DEFAULT_COMPANY_TIME_ZONE;
}

/**
 * The zone of the DEVICE the screen is running on. For one case only: a screen
 * that signs in as a device or by a link, with no user of the company - the
 * kiosk, the driver tablet, the contract and read-and-sign links, the care-home
 * portal. Its own sign-in call carries the company's zone (TODO 135); this is
 * what it shows in the moment before that call has answered, and when it cannot
 * be reached at all (a tablet with no signal).
 *
 * Why the device and not the platform default: a kiosk on the company's wall is
 * set to the company's own time far more often than the company is in the UK.
 * Never use it on a signed-in screen - that is what `useCompanyClock()` is for.
 */
export function deviceTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

/**
 * The company's date today, as `yyyy-mm-dd`, for code outside a component - a
 * module-level helper, an event handler, a document builder, a filename.
 *
 * The replacement for `new Date().toISOString().slice(0, 10)`, which is the
 * UTC date: from 00:00 to 01:00 BST it is yesterday, so a form defaulted to
 * yesterday and a letter printed then was dated the day before (TODO 135).
 */
export function companyTodayIso(at: When = new Date()): string {
  return companyDateIso(companyTimeZone(), at);
}

/**
 * A calendar day moved by whole days, months or years - by the calendar, never
 * through a clock. Takes and returns `yyyy-mm-dd`; an unreadable day gives ''.
 *
 * The replacement for `d.setDate(d.getDate() + 30)` followed by
 * `d.toISOString().slice(0, 10)`. That moves a LOCAL date and then reads it in
 * UTC, so the answer is a day early whenever local time is ahead of UTC at the
 * moment it lands on: from 00:00 to 01:00 all summer for "today plus thirty",
 * and at ANY time of day for a winter date that lands in summer - 15 January
 * plus six months gave 14 July. Overflow is JavaScript's own, as it was: 31
 * January plus a month runs on into March (the 3rd, or the 2nd in a leap year)
 * (TODO 135).
 */
export function shiftCalendarDate(
  day: string,
  by: { days?: number; months?: number; years?: number },
): string {
  const parts = calendarDateParts(day);
  if (!parts) return '';
  // setUTCFullYear, not Date.UTC: Date.UTC reads a year below 100 as 19xx.
  const moved = new Date(0);
  moved.setUTCFullYear(
    parts.y + (by.years ?? 0),
    parts.m - 1 + (by.months ?? 0),
    parts.d + (by.days ?? 0),
  );
  const year = moved.getUTCFullYear();
  if (Number.isNaN(moved.getTime()) || year < 0 || year > 9999) return '';
  return moved.toISOString().slice(0, 10);
}

/**
 * The year, month and day of a calendar date (`yyyy-mm-dd`, or a `@db.Date` as
 * `…T00:00:00.000Z`), or null when it is not one. Strict on purpose: a date
 * still being typed ("2026-10-"), a day that does not exist (30 February) and
 * anything that is not text in that shape are all unreadable, not a different
 * date - `Number('')` is 0, and day 0 is the last day of the month before.
 */
function calendarDateParts(day: string): { y: number; m: number; d: number } | null {
  const hit = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day));
  if (!hit) return null;
  const y = Number(hit[1]);
  const m = Number(hit[2]);
  const d = Number(hit[3]);
  const check = new Date(0);
  check.setUTCFullYear(y, m - 1, d);
  const real = check.getUTCFullYear() === y && check.getUTCMonth() === m - 1 && check.getUTCDate() === d;
  return real ? { y, m, d } : null;
}

/**
 * A calendar day as a Date at LOCAL midnight on that day, for code that builds
 * and reads a calendar by local parts (`new Date(y, m, d)`, `getDate()`).
 *
 * `new Date('2026-10-01')` is UTC midnight, which is 30 September in any browser
 * west of UTC - a contract expiring on the 1st sat on the 30th in the planner
 * there. Local in, local out is right in every zone, and this is the "local
 * in". For a CALENDAR DATE only (`yyyy-mm-dd`, or a `@db.Date` as
 * `…T00:00:00.000Z`): a moment goes through `companyDateIso` first. An
 * unreadable value gives an Invalid Date, as `new Date(value)` did (TODO 135).
 */
export function calendarDateLocal(day: string): Date {
  const parts = calendarDateParts(day);
  if (!parts) return new Date(NaN);
  // setFullYear, not new Date(y, m, d): that reads a year below 100 as 19xx.
  const local = new Date(2000, 0, 1);
  local.setFullYear(parts.y, parts.m - 1, parts.d);
  return local;
}

/**
 * The company's today as a Date at LOCAL midnight - the seed for a calendar
 * built by local parts, so the month it opens on and the day it marks as today
 * are the company's and not the viewer's computer's (TODO 135).
 */
export function companyTodayLocal(at: When = new Date()): Date {
  return calendarDateLocal(companyTodayIso(at));
}

/**
 * Whole calendar days from `today` (the company's date, `todayIso()`) to `day`,
 * a calendar date - `yyyy-mm-dd`, or a `@db.Date` as `…T00:00:00.000Z`. 0 is
 * today, -1 yesterday; an unreadable `day` gives NaN.
 *
 * The one rule for "how long until it expires / is due". Counted between two
 * calendar days, never from "now": an expiry arrives as UTC midnight, so hours
 * from now made every answer a day high from 00:00 to 01:00 BST, and something
 * that expired yesterday read as current. Both days are read at UTC midnight,
 * so the difference is always a whole number of days (TODO 135).
 */
export function daysFromToday(day: string, today: string): number {
  return Math.round(
    (Date.parse(`${day.slice(0, 10)}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`)) / 86_400_000,
  );
}

/**
 * A calendar date (`@db.Date`), formatted without moving the day: read as UTC
 * midnight and shown in UTC, for every company. See "AN INSTANT IS NOT A DAY".
 */
export function formatCalendarDate(
  value: Date | string,
  options: Intl.DateTimeFormatOptions = DATE,
  locale: string = DEFAULT_LOCALE,
): string {
  return formatInZone('UTC', value, options, locale);
}

export interface CompanyClock {
  /** The IANA zone in use: the company's once loaded, the default before. */
  timeZone: string;
  /** False until the company's own zone has loaded. */
  ready: boolean;
  /** An instant's date in the company's zone. Default `02 Oct 2026`. */
  formatDate(at: When, options?: Intl.DateTimeFormatOptions): string;
  /** An instant's time in the company's zone. Default `00:37`. */
  formatTime(at: When, options?: Intl.DateTimeFormatOptions): string;
  /** Both. Default `02 Oct 2026, 00:37`. */
  formatDateTime(at: When, options?: Intl.DateTimeFormatOptions): string;
  /** A `@db.Date`, never moved by the zone. Default `02 Oct 2026`. */
  formatCalendarDate(value: Date | string, options?: Intl.DateTimeFormatOptions): string;
  /** The company's date today (or at `at`), as `yyyy-mm-dd`. */
  todayIso(at?: When): string;
}

export function useCompanyClock(): CompanyClock {
  const zone = useQuery({
    queryKey: COMPANY_TIME_ZONE_KEY,
    queryFn: () => apiFetch<{ timezone: string }>('/api/tenant/time-zone'),
    // A zone changes rarely. Five minutes, and on focus, so a change made for
    // the company in Platform Admin reaches an open tab without a reload - the
    // lesson useCurrentTenant learned with `staleTime: Infinity`.
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
  });
  const timeZone = zone.data?.timezone ?? DEFAULT_COMPANY_TIME_ZONE;
  return {
    timeZone,
    ready: zone.data !== undefined,
    formatDate: (at, options = DATE) => formatInZone(timeZone, at, options),
    formatTime: (at, options = TIME) => formatInZone(timeZone, at, options),
    formatDateTime: (at, options = DATE_TIME) => formatInZone(timeZone, at, options),
    formatCalendarDate: (value, options = DATE) => formatCalendarDate(value, options),
    todayIso: (at = new Date()) => companyDateIso(timeZone, at),
  };
}
